import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SetCourseExpandCollapse } from '../../../data-grid-container/courses/types/courses.types';
import {
    manageCourseStrandIsExpanded,
    manageCourseTopicExpandCollapse
} from '../../helpers/courses/course-expand-helpers';
import {
    GridCells,
    GridCounters,
    ToggleActivityGridCheckbox,
    ToggleGridCell, ToggleStudentGridCheckbox,
    ToggleTopicGridCheckbox
} from '../../helpers/slice/topic/topic-slice.types';
import {
    SkillQuestsConstants,
    SkillQuestsState
} from './skill-quests.types';
import { RootState } from '@config/react-redux/store';
import { resetGridCells } from '@containers/activity-router/routes/helpers/grid/cells/grid-cells-helpers';
import {
    resetGridCellCounters
} from '@containers/activity-router/routes/helpers/grid/counters/grid-cell-counters/grid-cell-counters';
import { RouteHelpersModalConstants } from '@containers/activity-router/routes/helpers/modal/route-modal-helpers.types';
import { toggleGridCells } from '@containers/activity-router/routes/helpers/slice/grid/slice-grid-helpers';
import { defaultState } from '@containers/activity-router/routes/helpers/slice/slice-defaults';
import {
    toggleRouteActivityGridCheckbox,
    toggleRouteStudentGridCheckbox,
    toggleRouteTopicGridCheckbox
} from '@containers/activity-router/routes/helpers/slice/slice-helpers';
import { GridCount } from '@containers/activity-router/routes/helpers/slice/slice.types';
import {
    manageSkillsQuestCellAssignmentsSelected,
    manageSkillsQuestSelectedAssignments,
    updateSkillsQuestAssignments
} from '@containers/activity-router/routes/revise/skill-quests/helpers/slice/assignments/selected/assignments-selected-helpers';
import {
    TransformCourse,
    TransformedSkillsQuestAssignments
} from '@services/gateways/combined-assign-gateway/combined-assign-gateway.types';
import { UpfAssignCurriculumList } from '@services/gateways/upf-assign-gateway/upf-assign-gateway.types';

const { name } = SkillQuestsConstants;
const {
    studentCount,
    topicCount,
    topicActivityCount,
    activityCount
} = GridCount;
const initialState: SkillQuestsState = {
    course: {
        selected: null
    },
    grid: {
        cells: {},
        counters: {
            [studentCount]: {},
            [topicCount]: {},
            [activityCount]: {},
            [topicActivityCount]: {}
        }
    },
    assignments: {
        ...defaultState.assignments,
        grid: {}
    },
    modal: null,
    filters: {
        isExpanded: true,
        courseId: null,
        curriculumCourse: []
    }
};

export const skillQuestsSlice = createSlice({
    name,
    initialState,
    reducers: {
        setSkillQuestsCourseSelected (state, action: PayloadAction<TransformCourse>) {
            state.course.selected = action.payload;
        },
        setSkillQuestsCourseStrandIsExpanded (state, action: PayloadAction<SetCourseExpandCollapse>) {
            if (!state.course.selected) return;

            state.course.selected.topics = manageCourseStrandIsExpanded({
                course: state.course.selected,
                payload: action.payload
            });
        },
        setSkillQuestsCourseIsExpanded (state, action: PayloadAction<SetCourseExpandCollapse>) {
            if (!state.course.selected) return;

            state.course.selected.topics = manageCourseStrandIsExpanded({
                course: state.course.selected,
                payload: action.payload
            });
        },
        setChallengesGridCells (state, action: PayloadAction<GridCells>) {
            state.grid.cells = action.payload;
        },
        setSkillQuestsGridCells (state, action: PayloadAction<GridCells>) {
            state.grid.cells = action.payload;
        },
        setSkillQuestsGridCountersInitialValue (state, action: PayloadAction<GridCounters>) {
            state.grid.counters = action.payload;
        },
        toggleSkillQuestsGridCells (state, action: PayloadAction<ToggleGridCell>) {
            const {
                student,
                ...payload
            } = action.payload;
            const {
                gridCells,
                gridCounters
            } = toggleGridCells({
                gridCells: state.grid.cells,
                gridCounters: state.grid.counters,
                studentId: student.userProfileId.toString(),
                ...payload
            });

            state.grid.cells = gridCells;
            state.grid.counters = gridCounters;
            state.assignments.selected = manageSkillsQuestCellAssignmentsSelected({
                ...action.payload,
                studentAssignments: state.assignments.grid,
                gridCells: state.grid.cells,
                selectedAssignments: state.assignments.selected
            });
        },
        toggleSkillQuestsStudentGridCheckbox (state, action: PayloadAction<ToggleStudentGridCheckbox>) {
            const {
                gridCells,
                gridCounters,
                assignmentsSelected
            } = toggleRouteStudentGridCheckbox({
                payload: action.payload,
                state: {
                    gridCells: state.grid.cells,
                    gridCounters: state.grid.counters,
                    assignmentsSelected: state.assignments.selected
                },
                manageSelectedAssignments: (params) => manageSkillsQuestSelectedAssignments({
                    ...params,
                    studentAssignments: state.assignments.grid
                })
            });

            state.grid.cells = gridCells;
            state.grid.counters = gridCounters;
            state.assignments.selected = assignmentsSelected;
        },
        toggleSkillQuestsTopicGridCheckbox (state, action: PayloadAction<ToggleTopicGridCheckbox>) {
            const {
                gridCells,
                gridCounters,
                assignmentsSelected
            } = toggleRouteTopicGridCheckbox({
                payload: action.payload,
                state: {
                    gridCells: state.grid.cells,
                    gridCounters: state.grid.counters,
                    assignmentsSelected: state.assignments.selected
                },
                manageSelectedAssignments: (params) => manageSkillsQuestSelectedAssignments({
                    ...params,
                    studentAssignments: state.assignments.grid
                })
            });

            state.grid.cells = gridCells;
            state.grid.counters = gridCounters;
            state.assignments.selected = assignmentsSelected;
        },
        toggleSkillQuestsActivityGridCheckbox (state, action: PayloadAction<ToggleActivityGridCheckbox>) {
            const {
                gridCells,
                gridCounters,
                assignmentsSelected
            } = toggleRouteActivityGridCheckbox({
                payload: action.payload,
                state: {
                    gridCells: state.grid.cells,
                    gridCounters: state.grid.counters,
                    assignmentsSelected: state.assignments.selected
                },
                manageSelectedAssignments: (params) => manageSkillsQuestSelectedAssignments({
                    ...params,
                    studentAssignments: state.assignments.grid
                })
            });

            state.grid.cells = gridCells;
            state.grid.counters = gridCounters;
            state.assignments.selected = assignmentsSelected;
        },
        setSkillQuestsAssignmentGrid (state, action: PayloadAction<TransformedSkillsQuestAssignments>) {
            state.assignments.grid = action.payload;
        },
        resetSkillQuestsAssignments (state) {
            state.assignments = {
                grid: {},
                selected: defaultState.assignments.selected
            };
            state.filters.isExpanded = true;
        },
        resetSkillQuestsGrid (state) {
            state.grid.cells = resetGridCells({
                gridCells: state.grid.cells,
                isSelected: false
            });
            state.grid.counters = resetGridCellCounters({
                counters: state.grid.counters
            });
        },
        resetSkillQuestsSelectedAssignments (state) {
            state.assignments.selected = defaultState.assignments.selected;
        },
        setSkillQuestsModal (state, action: PayloadAction<RouteHelpersModalConstants | null>) {
            state.modal = action.payload;
        },
        setSkillQuestsFiltersCourseId (state, action: PayloadAction<string>) {
            state.filters.courseId = action.payload;
        },
        setSkillQuestFilterCurriculumCourse (state, action: PayloadAction<UpfAssignCurriculumList>) {
            state.filters.curriculumCourse = action.payload;
        },
        setSkillsQuestIsExpanded (state, action: PayloadAction<boolean>) {
            state.filters.isExpanded = action.payload;
            if (state.course.selected) {
                state.course.selected = manageCourseTopicExpandCollapse({
                    course: state.course.selected,
                    isExpanded: action.payload
                });
            }
        },
        updateSkillQuestsAssignment (state, action: PayloadAction<TransformedSkillsQuestAssignments>) {
            state.assignments.selected = updateSkillsQuestAssignments({
                studentAssignments: action.payload,
                selectedAssignments: state.assignments.selected,
                gridCells: state.grid.cells
            });
        }
    }
});

/**
 * ACTIONS
 */
export const {
    setSkillQuestsCourseIsExpanded,
    setSkillQuestsCourseSelected,
    setSkillQuestsGridCells,
    toggleSkillQuestsStudentGridCheckbox,
    setSkillQuestsGridCountersInitialValue,
    toggleSkillQuestsGridCells,
    toggleSkillQuestsTopicGridCheckbox,
    toggleSkillQuestsActivityGridCheckbox,
    setSkillQuestsModal,
    setSkillQuestsFiltersCourseId,
    setSkillQuestFilterCurriculumCourse,
    setSkillsQuestIsExpanded,
    setSkillQuestsAssignmentGrid,
    resetSkillQuestsAssignments,
    resetSkillQuestsGrid,
    resetSkillQuestsSelectedAssignments,
    updateSkillQuestsAssignment
} = skillQuestsSlice.actions;

/**
 * SELECTORS
 */
export const selectSkillQuestsCourseSelected = (state: RootState) => state[name].course.selected;

export const selectSkillQuestsGridCounters = (state: RootState) => state[name].grid.counters;

export const selectSkillQuestsGridCells = (state: RootState) => state[name].grid.cells;

export const selectSkillQuestsAssignmentsGrid = (state: RootState) => state[name].assignments.grid;

export const selectSkillQuestsAssignmentsSelected = (state: RootState) => state[name].assignments.selected;

export const selectSkillQuestsModal = (state: RootState) => state[name].modal;

export const selectSkillQuestsFiltersCourseId = (state: RootState) => state[name].filters.courseId;

export const selectSkillQuestFilterCurriculumCourse = (state: RootState) => state[name].filters.curriculumCourse;

export const selectSkillsQuestIsExpanded = (state: RootState) => state[name].filters.isExpanded;

export default skillQuestsSlice.reducer;
